/**
 * Deal API (P7 — Deal Operations Dashboard).
 *
 * 딜 목록/상세/패치, 구성(Configuration)/품목, 딜 이벤트/상태 이력.
 * 공통 fetch 헬퍼는 ./http, 채팅 스레드 타입은 ./chat-api 에서 import.
 */

import { API_BASE, authHeaders, getJson, postJson, patchJson, loadingFetch, ApiConflictError, parseResponse, httpError, errorDetail } from './http';
import {
  DealResponseSchema, DealFullResponseSchema, DealListResponseSchema,
  DealItemSchema, DealConfigurationSchema, DealItemsReplaceResponseSchema,
  DealStatusHistoryResponseSchema, DealEventsResponseSchema,
  IdResponseSchema, OkResponseSchema,
  type DealRow, type DealItem, type DealConfiguration, type DealFull,
  type DealStatus, type DealClassification,
  type DealStatusHistoryEntry, type DealEvent,
} from './schemas';

// ─── Deal 타입 — shared z.infer가 정본(schemas.ts 관문), 손 정의 제거(2026-07-12) ──
// 모든 응답은 zod safeParse 경계 1회 통과 — 이후 신뢰.
export type {
  DealRow, DealItem, DealConfiguration, DealFull,
  DealStatus, DealClassification, DealStatusHistoryEntry, DealEvent,
};

// DealRow/DealItem/DealConfiguration/DealFull — shared z.infer 정본 (위 re-export 참조).

export interface DealListResponse {
  rows: DealRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DealListQuery {
  search?: string;
  status?: DealStatus;
  classification?: DealClassification;
  assignee_user_id?: number;
  unassigned_only?: 'true' | 'false';
  open_only?: 'true' | 'false';
  is_active?: 'true' | 'false';
  watched?: 'true' | 'false';   // Watchlist 필터 (on 인 딜만)
  sort?: 'id' | 'name' | 'status' | 'classification' | 'assignee_name'
       | 'customer_name' | 'expected_close_date' | 'final_amount_krw'
       | 'created_at' | 'updated_at';
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export type DealPatch = Partial<{
  name: string;
  status: DealStatus;
  cancel_reason: string;                   // status='취소' 로 바꿀 때 함께 (마이그 115)
  classification: DealClassification | null;
  assignee_user_id: number | null;
  customer_id: number | null;
  contact_id: number | null;
  expected_close_date: string | null;
  notes: string | null;
  inquiry_channel: string | null;          // 유입 경로
  communication_channel: string | null;    // 커뮤니케이션 채널
}>;

// ─── Deal API 함수 ───────────────────────────────────────────────────
/** GET /api/admin/deal — 목록 (v_deal_list JOIN 포함) */
export async function fetchDealList(query: DealListQuery = {}): Promise<DealListResponse> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
  }
  const s = qs.toString();
  return getJson<DealListResponse>(`/api/admin/deal${s ? `?${s}` : ''}`, { schema: DealListResponseSchema });
}

/** PUT /api/admin/deal/:id/watch — Watchlist 별표 토글 (개인 설정) */
export async function setDealWatch(
  id: string | number,
  watched: boolean,
): Promise<void> {
  await patchJson<{ ok: boolean }>(
    `/api/admin/deal/${encodeURIComponent(String(id))}/watch`,
    { watched },
    OkResponseSchema,
  );
}

/**
 * GET /api/admin/deal/:id/full — 상세 (deal + items + threads 최신 30개).
 *
 * signal — Sola 라우터 loader 에서 호출 시 AbortSignal 전달.
 * 라우트 이동으로 취소되면 fetch 가 AbortError 던지고 즉시 중단됨.
 */
export async function fetchDealFull(
  id: string | number,
  opts: { signal?: AbortSignal } = {},
): Promise<DealFull> {
  return getJson<DealFull>(
    `/api/admin/deal/${encodeURIComponent(String(id))}/full`,
    { ...opts, schema: DealFullResponseSchema },
  );
}

/** PATCH /api/admin/deal/:id — 부분 수정 (status, assignee 등) */
export async function patchDeal(id: string | number, patch: DealPatch): Promise<DealRow> {
  return patchJson<DealRow>(`/api/admin/deal/${encodeURIComponent(String(id))}`, patch, DealResponseSchema);
}

/**
 * POST /api/admin/deal — 빈 드래프트 딜 생성 (딜 생성 페이지 진입용).
 * name 만 필수라 placeholder 로 만들고, 채팅/수기로 채운다. 반환 id 로 상세 진입.
 */
export async function createDraftDeal(): Promise<{ id: string }> {
  return postJson<{ id: string }>('/api/admin/deal', { name: '새 딜' }, IdResponseSchema);
}

/** DELETE /api/admin/deal/:id — soft delete (딜 생성 취소 시 드래프트 삭제). */
export async function deleteDeal(id: string | number): Promise<void> {
  const res = await loadingFetch(
    `${API_BASE}/api/admin/deal/${encodeURIComponent(String(id))}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  if (!res.ok) {
    throw await httpError(res);
  }
}

/**
 * POST /api/admin/deal/:id/cleanup-draft — 딜 생성 페이지 이탈 시 호출.
 * 서버가 "빈 드래프트('새 딜'+거래처X+품목0)" 면 비활성화(리스트 숨김), 내용 있으면 유지.
 * 이탈 중 호출이라 결과는 신경 안 씀(best-effort). keepalive=true 면 탭 닫힘 중에도 전송 시도.
 */
export function cleanupDraftIfEmpty(id: string | number, opts?: { keepalive?: boolean }): void {
  try {
    void fetch(
      `${API_BASE}/api/admin/deal/${encodeURIComponent(String(id))}/cleanup-draft`,
      { method: 'POST', headers: authHeaders(), keepalive: opts?.keepalive ?? false },
    ).catch(() => { /* 이탈 중 — 무시 */ });
  } catch { /* best-effort */ }
}

/** POST /api/admin/deal/:id/restart-config — 구성 재시작(견적 반려·구성 취소·상태 리셋) */
export async function restartDealConfig(id: string | number): Promise<DealRow> {
  return postJson<DealRow>(
    `/api/admin/deal/${encodeURIComponent(String(id))}/restart-config`, {}, DealResponseSchema);
}

/** POST /api/admin/deal/:id/cancel — 딜 취소(사유 + 상태 '취소'). expectedVersion=낙관적 잠금 */
export async function cancelDeal(
  id:              string | number,
  reason:          string,
  expectedVersion?: number,
): Promise<DealRow> {
  return postJson<DealRow>(
    `/api/admin/deal/${encodeURIComponent(String(id))}/cancel`,
    { reason, expected_version: expectedVersion },
    DealResponseSchema,
  );
}

// ─── 구성(Configuration) / 품목 단건 API (B3) ────────────────────────

/** PATCH /api/admin/deal/deal-item/:itemId — 판매 단가 등 단건 수정 */
export async function patchDealItem(
  itemId: string | number,
  patch:  { sell_unit_price_krw?: number | null; quantity?: number | null; notes?: string | null },
): Promise<DealItem> {
  return patchJson<DealItem>(
    `/api/admin/deal/deal-item/${encodeURIComponent(String(itemId))}`,
    patch,
    DealItemSchema,
  );
}

/** POST /api/admin/deal/:id/configurations — 새 구성 (기본: 최저 config_no 복제) */
export async function createDealConfiguration(
  dealId: string | number,
  body:   { name?: string; duplicate_from_config_id?: string | number } = {},
): Promise<DealConfiguration> {
  return postJson<DealConfiguration>(
    `/api/admin/deal/${encodeURIComponent(String(dealId))}/configurations`,
    body,
    DealConfigurationSchema,
  );
}

/** PATCH /api/admin/deal/configurations/:configId — 이름/잠금(품목 확정) */
export async function patchDealConfiguration(
  configId: string | number,
  patch:    { name?: string; is_locked?: boolean },
): Promise<DealConfiguration> {
  return patchJson<DealConfiguration>(
    `/api/admin/deal/configurations/${encodeURIComponent(String(configId))}`,
    patch,
    DealConfigurationSchema,
  );
}

/** DELETE /api/admin/deal/configurations/:configId — 구성 삭제 (마지막 1개 불가) */
export async function deleteDealConfiguration(configId: string | number): Promise<void> {
  const res = await loadingFetch(
    `${API_BASE}/api/admin/deal/configurations/${encodeURIComponent(String(configId))}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  if (!res.ok) {
    throw await httpError(res);
  }
}

/** 편집 모드 '완료' — 구성 품목 배열을 순서대로 일괄 교체(diff) */
export interface DealConfigItemInput {
  id?:                  string;          // 기존 품목이면 id, 신규면 생략
  category?:            string | null;
  option_part?:         string | null;
  item_description?:    string | null;
  quantity?:            number | null;
  requested_condition?: string | null;
  sell_unit_price_krw?: number | null;
  is_unregistered?:     boolean;         // 마이그 107 (§4.5)
  notes?:               string | null;
}

/**
 * PUT /api/admin/deal/configurations/:configId/items — 구성 품목 일괄 교체.
 * expectedVersion: 편집 시작 시점의 deal.version (마이그 114) — 불일치 시
 * 서버가 409 → ApiConflictError throw (저장 핸들러가 병합 UX 분기).
 */
export async function replaceDealConfigItems(
  configId:         string | number,
  items:            DealConfigItemInput[],
  expectedVersion?: number,
): Promise<DealItem[]> {
  const res = await fetch(
    `${API_BASE}/api/admin/deal/configurations/${encodeURIComponent(String(configId))}/items`,
    {
      method:  'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body:    JSON.stringify({ items, expected_version: expectedVersion }),
    },
  );
  if (!res.ok) {
    const bodyDetail = errorDetail(await res.json().catch(() => null));
    if (res.status === 409) {
      throw new ApiConflictError(bodyDetail || '딜이 다른 곳에서 수정되었습니다.');
    }
    throw new Error(`HTTP ${res.status} ${bodyDetail || ''}`.trim());
  }
  const data = parseResponse(await res.json(), DealItemsReplaceResponseSchema, 'PUT /configurations/:id/items');
  return data.items;
}

// ─── 딜 상태 이력 / 활동 히스토리 ────────────────────────────────────

// DealStatusHistoryEntry/DealEvent — schemas.ts z.infer 정본 (위 re-export 참조).

/** GET /api/admin/deal/:id/status-history — 상태 전이 이력(시간순) */
export async function fetchDealStatusHistory(
  id: string | number,
): Promise<DealStatusHistoryEntry[]> {
  const r = await getJson<{ history: DealStatusHistoryEntry[] }>(
    `/api/admin/deal/${encodeURIComponent(String(id))}/status-history`,
    { silent: true, schema: DealStatusHistoryResponseSchema },   // 팝오버 자체 로딩 표시라 글로벌 펄 제외
  );
  return r.history;
}

/** GET /api/admin/deal/:id/events — 딜 활동 히스토리(최신순). */
export async function fetchDealEvents(id: string | number): Promise<DealEvent[]> {
  const r = await getJson<{ events: DealEvent[] }>(
    `/api/admin/deal/${encodeURIComponent(String(id))}/events`,
    { schema: DealEventsResponseSchema },
  );
  return r.events;
}
